// Append-only audit log at ~/.avatar/audit.log. Records sensitive actions
// (secrets set/rm, tool install/remove) WITHOUT logging secret values.
// Used by `avatar doctor` to surface recent activity.
import { promises as fs } from "node:fs";
import { AUDIT_LOG_PATH, ensureAvatarHome } from "./user-config-store.js";

// Union khai báo theo thực tế caller (verified qua grep `appendAuditEntry("...")`).
// Thêm action mới: vừa update union vừa update caller — TypeScript sẽ enforce.
// "logout", "secret_*", "tool_*", "sync", "restore" reserved cho future commands
// (đã được spec ở blueprint nhưng caller chưa wire — giữ trong union để command
// mới chỉ cần thêm caller, không cần đổi type).
export type AuditAction =
  | "login"
  | "login_reset"
  | "logout"
  | "secret_set"
  | "secret_rm"
  | "tool_install"
  | "tool_remove"
  | "init"
  | "sync"
  | "restore"
  | "uninstall"
  | "ai_setup"
  | "gitnexus_setup"
  | "cli_update"
  | "bootstrap";

export interface AuditEntry {
  timestamp: string;
  action: AuditAction;
  detail?: string;
}

// v1.14.0 SECURITY: chmod 600 audit.log. Default `fs.appendFile` không set mode
// → file world-readable (umask thông thường 644). Audit entries có thể chứa email,
// path tuyệt đối từ error messages → defense-in-depth chmod 600 (owner-only).
// Best-effort: Windows/WSL ignore chmod nhưng không throw → safe.
async function ensureAuditLogPermissions(): Promise<void> {
  try {
    await fs.chmod(AUDIT_LOG_PATH, 0o600);
  } catch {
    // File chưa tồn tại lần đầu (appendFile sẽ tạo) hoặc Windows skip chmod.
  }
}

export async function appendAuditEntry(action: AuditAction, detail?: string): Promise<void> {
  await ensureAvatarHome();
  const entry: AuditEntry = {
    timestamp: new Date().toISOString(),
    action,
    ...(detail ? { detail } : {}),
  };
  const line = `${JSON.stringify(entry)}\n`;
  await fs.appendFile(AUDIT_LOG_PATH, line, { encoding: "utf8", mode: 0o600 });
  // Re-enforce chmod 600 vì appendFile mode chỉ apply khi tạo file mới — file
  // pre-exist với mode khác sẽ không bị thay đổi. Idempotent + cheap.
  await ensureAuditLogPermissions();
}
